/**
 * SlotDepositModal — модалка запуска Venture Slot.
 *
 * Показывает:
 *   - Большую иконку слота + название
 *   - Фиксированный номинал контракта (нельзя изменить, это by design)
 *   - Месячную/дневную доходность
 *   - Лимит ×2.5 total payout
 *   - Кнопку "Запустить через TON"
 *   - Подсказку что после запуска контракт уходит в эфир
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Zap } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import type { VentureSlotInfo } from '@/data/ventureSlots';
import { lunaApi } from '@/services/api';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useUISounds } from '@/hooks/useUISounds';

interface SlotDepositModalProps {
  slot: VentureSlotInfo | null;
  onClose: () => void;
  onConfirm: () => void;
}

function formatUSDT(v: number, locale: string): string {
  return v.toLocaleString(locale);
}

export function SlotDepositModal({ slot, onClose, onConfirm }: SlotDepositModalProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const { haptic } = useTelegramWebApp();
  const uiSounds = useUISounds();
  const open = slot !== null;
  const [confirmed, setConfirmed] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState('');

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setConfirmed(false);
      setPurchasing(false);
      setError('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const [activating, setActivating] = useState(false);
  const timersRef = useRef<number[]>([]);

  useEffect(() => {
    if (!open) { timersRef.current.forEach(clearTimeout); timersRef.current = []; }
  }, [open]);

  const schedule = (fn: () => void, ms: number) => {
    timersRef.current.push(window.setTimeout(fn, ms));
  };

  const handleConfirmClick = async () => {
    if (!slot || purchasing) return;
    setPurchasing(true);
    setError('');
    try {
      await lunaApi.slots.purchase(slot.dbId);
      // Анимация 7 сек как раньше. Новый 12-сек AirPods Pro ×
      // Cyberpunk семпл продолжит звучать поверх второй фазы —
      // хвост затухает физическим fade-out, так что наложение
      // slotConfirmed на tail'е не диссонирует.
      setActivating(true);
      haptic.success();
      uiSounds.slotDepositConfirm();
      schedule(() => haptic.light(), 800);
      schedule(() => haptic.light(), 1500);
      schedule(() => haptic.medium(), 2500);
      schedule(() => haptic.light(), 3500);
      schedule(() => haptic.light(), 4500);
      schedule(() => haptic.medium(), 5500);
      schedule(() => haptic.success(), 6500);
      schedule(() => {
        setActivating(false);
        setConfirmed(true);
        haptic.success();
        uiSounds.slotConfirmed();
      }, 7000);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message || t('slotDeposit.purchaseError');
      setError(msg);
      haptic.error();
      uiSounds.toastError();
    } finally {
      setPurchasing(false);
    }
  };

  const handleContinueTap = () => {
    haptic.light();
    onConfirm();
  };

  return (
    <AnimatePresence>
      {open && slot && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={() => { uiSounds.bellClose(); onClose(); }}
        >
          <div className="absolute inset-0 bg-black/65 backdrop-blur-sm [.platform-non-ios_&]:bg-black/90 [.platform-non-ios_&]:backdrop-blur-none" />

          <motion.div
            className="relative w-full max-w-sm rounded-[28px]"
            style={{
              background:
                'linear-gradient(180deg, var(--glass-fill-strong) 0%, var(--glass-fill) 100%)',
              border: '1px solid var(--glass-stroke-strong)',
              boxShadow: `var(--glass-shadow), 0 0 80px -10px hsl(${slot.hue} 80% 60% / 0.45), inset 0 1.5px 0 0 var(--glass-rim-light), inset 0 -1px 0 0 var(--glass-rim-dark)`,
              backdropFilter: 'none',
              WebkitBackdropFilter: 'none',
              overflow: 'visible',
            }}
            initial={{ y: 30, opacity: 0, scale: 0.94 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0, scale: 0.94 }}
            transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* ─── Закрыть × ─── */}
            {!activating && !confirmed && (
              <button
                type="button"
                onClick={() => { uiSounds.bellClose(); onClose(); }}
                data-no-sound="true"
                aria-label={t('slotDeposit.closeAria')}
                style={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  zIndex: 30,
                }}
                className="lg-pill h-8 w-8 flex items-center justify-center text-[var(--text-secondary)] text-[16px] leading-none"
              >
                ×
              </button>
            )}

            {/* ─── Activating — 7 sec WOW animation ─── */}
            {activating && slot && (
              <motion.div
                className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-[28px] overflow-hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{
                  background: 'linear-gradient(180deg, rgba(8,8,16,0.97) 0%, rgba(12,12,24,0.99) 100%)',
                  backdropFilter: 'blur(24px)',
                }}
              >
                {/* Расходящиеся кольца */}
                {[0, 1, 2, 3, 4, 5].map(i => (
                  <motion.div
                    key={`ar-${i}`}
                    className="absolute rounded-full"
                    style={{
                      left: '50%', top: '50%',
                      width: 40, height: 40,
                      marginLeft: -20, marginTop: -20,
                      border: `1.5px solid hsl(${slot.hue} 80% 65%)`,
                    }}
                    animate={{ scale: [1, 5 + i], opacity: [0.5, 0] }}
                    transition={{ duration: 3, delay: i * 0.5, ease: [0.22, 1, 0.36, 1], repeat: 1, repeatDelay: 0.3 }}
                  />
                ))}

                {/* Частицы конвергируют */}
                {[...Array(12)].map((_, i) => {
                  const angle = (i / 12) * Math.PI * 2;
                  return (
                    <motion.div
                      key={`ap-${i}`}
                      className="absolute rounded-full"
                      style={{
                        width: 4, height: 4, left: '50%', top: '50%',
                        background: `hsl(${slot.hue} 80% 65%)`,
                        boxShadow: `0 0 12px hsl(${slot.hue} 80% 60%)`,
                      }}
                      animate={{
                        x: [Math.cos(angle) * 150, 0],
                        y: [Math.sin(angle) * 150, 0],
                        opacity: [0, 1, 1, 0], scale: [0.3, 1.5, 1, 0],
                      }}
                      transition={{ duration: 4, delay: 1 + i * 0.15, ease: [0.32, 0.72, 0, 1] }}
                    />
                  );
                })}

                {/* Иконка слота пульсирует */}
                <motion.img
                  src={slot.icon}
                  alt={slot.name}
                  className="relative z-10"
                  style={{ width: 100, height: 100, filter: `drop-shadow(0 0 30px hsl(${slot.hue} 80% 60%))` }}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: [0, 1.1, 1, 1.15, 1.05, 1.2, 1], opacity: 1 }}
                  transition={{ duration: 6, delay: 0.5, ease: 'easeInOut' }}
                />

                {/* Статусы */}
                {[
                  { text: t('slotDeposit.step1'), d: 0 },
                  { text: t('slotDeposit.step2'), d: 1.5 },
                  { text: t('slotDeposit.step3'), d: 3 },
                  { text: t('slotDeposit.step4'), d: 4.5 },
                  { text: t('slotDeposit.step5'), d: 6 },
                ].map((s, i) => (
                  <motion.p
                    key={i}
                    className="absolute text-[10px] uppercase tracking-[0.25em] font-mono font-bold"
                    style={{ top: '72%', color: i === 4 ? '#fff' : `hsl(${slot.hue} 80% 65%)` }}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: [0, 1, 1, 0], y: [8, 0, 0, -8] }}
                    transition={{ duration: i === 4 ? 1.5 : 1.2, delay: s.d, times: [0, 0.15, 0.7, 1] }}
                  >
                    {s.text}
                  </motion.p>
                ))}
              </motion.div>
            )}

            {/* ─── Success overlay — after 7sec activation ─── */}
            {confirmed && (
              <motion.div
                className="absolute inset-0 z-20 flex flex-col items-center justify-center px-6 rounded-[28px] overflow-hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.25 }}
                style={{
                  background:
                    'linear-gradient(180deg, rgba(8, 8, 16, 0.96) 0%, rgba(18, 18, 30, 0.98) 100%)',
                  backdropFilter: 'blur(24px) saturate(160%)',
                  WebkitBackdropFilter: 'none',
                }}
              >
                {/* 3 expanding rings — colored by slot hue */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div
                    className="claim-success-ring claim-success-ring-1"
                    style={{ borderColor: `hsl(${slot.hue} 85% 65%)` }}
                  />
                  <div
                    className="claim-success-ring claim-success-ring-2"
                    style={{ borderColor: `hsl(${slot.hue} 80% 55%)` }}
                  />
                  <div
                    className="claim-success-ring claim-success-ring-3"
                    style={{ borderColor: `hsl(${slot.hue} 75% 75%)` }}
                  />
                </div>

                {/* Confetti particles — 18 radial dots */}
                <div
                  className="absolute pointer-events-none"
                  style={{
                    top: '50%',
                    left: '50%',
                    width: 0,
                    height: 0,
                  }}
                >
                  {Array.from({ length: 18 }).map((_, i) => {
                    const angle = (i / 18) * Math.PI * 2;
                    const distance = 150;
                    const dx = Math.cos(angle) * distance;
                    const dy = Math.sin(angle) * distance;
                    return (
                      <span
                        key={i}
                        className="claim-particle"
                        style={
                          {
                            '--dx': `${dx}px`,
                            '--dy': `${dy}px`,
                            animationDelay: `${0.1 + i * 0.02}s`,
                            background: `hsl(${slot.hue} 85% 65%)`,
                            boxShadow: `0 0 12px hsl(${slot.hue} 85% 60%)`,
                          } as CSSProperties
                        }
                      />
                    );
                  })}
                </div>

                {/* Big success icon — inline SVG so there's no
                    square webp artifact, clean emerald neon glow */}
                <motion.div
                  className="relative z-10 flex items-center justify-center"
                  style={{ width: 168, height: 168 }}
                  initial={{ scale: 0, rotate: -30, opacity: 0 }}
                  animate={{ scale: 1, rotate: 0, opacity: 1 }}
                  transition={{
                    duration: 0.7,
                    ease: [0.32, 1.6, 0.32, 1],
                  }}
                >
                  <svg
                    viewBox="0 0 100 100"
                    width="168"
                    height="168"
                    aria-hidden
                    style={{
                      filter:
                        'drop-shadow(0 0 46px rgba(0, 255, 154, 0.85)) drop-shadow(0 0 16px rgba(94, 234, 212, 0.9))',
                    }}
                  >
                    <defs>
                      <linearGradient id="slot-success-ring" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#00ff9a" />
                        <stop offset="100%" stopColor="#5eead4" />
                      </linearGradient>
                    </defs>
                    {/* Outer circle */}
                    <circle
                      cx="50" cy="50" r="42"
                      fill="none"
                      stroke="url(#slot-success-ring)"
                      strokeWidth="4"
                    />
                    {/* Checkmark */}
                    <path
                      d="M30 52 L44 66 L72 36"
                      fill="none"
                      stroke="url(#slot-success-ring)"
                      strokeWidth="7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </motion.div>

                {/* Text block — fade-in + slide-up */}
                <motion.div
                  className="relative z-10 text-center mt-4"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, delay: 0.35, ease: [0.32, 0.72, 0, 1] }}
                >
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                    {t('slotDeposit.step5')}
                  </p>
                  <h3 className="font-display text-[26px] font-extrabold text-[var(--text-primary)] tracking-tight mt-1.5">
                    {slot.name}
                  </h3>
                  <p className="text-[12px] text-[var(--text-secondary)] mt-3 text-center max-w-[290px] leading-[1.7] mx-auto">
                    <Trans
                      i18nKey="slotDeposit.doneDescHTML"
                      values={{ amount: formatUSDT(slot.amount, locale), rate: slot.monthlyRate, cap: formatUSDT(slot.amount * 2.5, locale) }}
                      components={{ b: <span className="font-extrabold text-[var(--text-primary)]" />, br: <br />, span: <span className="whitespace-nowrap" /> }}
                    />
                  </p>
                </motion.div>

                {/* Pill at bottom — emerald green to match check.
                    Текст внутри приподнят на 4px: top 4px, bottom 12px
                    (сохраняет общую высоту капсулы). */}
                <motion.div
                  className="relative z-10 mt-2 rounded-full px-5 pt-[4px] pb-[12px]"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.7, ease: [0.32, 1.4, 0.32, 1] }}
                  style={{
                    background: 'rgba(0, 255, 154, 0.14)',
                    border: '1px solid rgba(94, 234, 212, 0.55)',
                    boxShadow: '0 0 30px -4px rgba(0, 255, 154, 0.55)',
                  }}
                >
                  <span className="font-mono text-[11px] font-extrabold uppercase tracking-[0.14em] text-[var(--status-positive)]">
                    + {formatUSDT(slot.amount, locale)} USDT · в работе
                  </span>
                </motion.div>

                {/* Continue button — fades in after all other anims
                    so the user has time to screenshot before tapping. */}
                <motion.div
                  className="relative z-10 w-full mt-6 px-2"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, delay: 1.2, ease: [0.32, 0.72, 0, 1] }}
                >
                  <button
                    type="button"
                    onClick={handleContinueTap}
                    className="w-full rounded-[var(--radius-pill)] font-display text-[13px] font-extrabold tracking-[0.16em] uppercase text-[var(--text-primary)] flex items-center justify-center leading-none transition-transform active:scale-[0.95]"
                    style={{
                      height: 52,
                      paddingTop: 0,
                      paddingBottom: 0,
                      background:
                        'linear-gradient(180deg, var(--glass-fill-strong), var(--glass-fill))',
                      border: '1px solid var(--glass-stroke-strong)',
                      boxShadow:
                        'inset 0 1px 0 0 rgba(255,255,255,0.18), 0 8px 24px -8px rgba(0,0,0,0.4)',
                    }}
                  >
                    <span className="translate-y-[0.5px]">Continue</span>
                  </button>
                  <p className="mt-2 text-center text-[9px] text-[var(--text-disabled)] font-mono uppercase tracking-[0.16em]">
                    screenshot · share · continue
                  </p>
                </motion.div>
              </motion.div>
            )}

            {/* Icon + name at top */}
            <div className="px-5 pt-5 pb-3 flex flex-col items-center text-center">
              <div
                className="flex items-center justify-center rounded-[28px] mb-3"
                style={{
                  width: 120,
                  height: 120,
                  background: `hsl(${slot.hue} 70% 58% / 0.22)`,
                  border: `2px solid hsl(${slot.hue} 80% 65%)`,
                  boxShadow: `0 0 64px -8px hsl(${slot.hue} 80% 60% / 0.75), inset 0 1.5px 0 0 rgba(255,255,255,0.28)`,
                }}
              >
                <img
                  src={slot.icon}
                  alt={slot.name}
                  width={84}
                  height={84}
                  style={{ filter: `drop-shadow(0 0 18px hsl(${slot.hue} 85% 65% / 0.9))` }}
                />
              </div>
              <p className="text-[9px] uppercase tracking-[0.2em] text-[var(--text-muted)] font-mono">
                {t('slotDeposit.contractLine', { month: slot.unlockMonth })}
              </p>
              <h2 className="font-display text-[26px] font-extrabold text-[var(--text-primary)] tracking-tight mt-1">
                {slot.name}
              </h2>
              <p className="text-[12px] text-[var(--text-secondary)] mt-1">
                {slot.tagline}
              </p>
            </div>

            {/* Deposit amount — big & fixed */}
            <div className="mx-5 mb-3 rounded-[18px] py-3 px-4 text-center"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid var(--glass-stroke)',
              }}
            >
              <p className="text-[9px] uppercase tracking-[0.2em] text-[var(--text-muted)] font-mono mb-1">
                {t('slotDeposit.nominal')}
              </p>
              <div className="flex items-baseline justify-center gap-1">
                <span className="font-display text-[32px] font-extrabold text-[var(--text-primary)] tabular-nums tracking-tight">
                  {formatUSDT(slot.amount, locale)}
                </span>
                <span className="text-[13px] font-extrabold text-[var(--text-muted)]">
                  USDT
                </span>
              </div>
              <div className="mt-2 text-[9px] text-[var(--text-disabled)] font-mono uppercase tracking-[0.14em]">
                фиксированный · прописан в контракте
              </div>
            </div>

            {/* 3 inline stats */}
            <div className="mx-5 mb-4 grid grid-cols-3 gap-2">
              <StatRow
                label={t('slotDeposit.yield')}
                value={`0,01–${slot.monthlyRate}%`}
                hue={slot.hue}
              />
              <StatRow
                label={t('slotDeposit.perDay')}
                value={`~${slot.dailyRate}%`}
                hue={slot.hue}
              />
              <StatRow
                label={t('slotDeposit.cap')}
                value={`${formatUSDT(slot.amount * 2.5, locale)}`}
                hue={slot.hue}
              />
            </div>

            {/* Action button */}
            <div className="px-6 pb-5">
              <button
                type="button"
                onClick={handleConfirmClick}
                disabled={confirmed || purchasing}
                className="w-full rounded-[var(--radius-pill)] py-4 font-display text-[14px] font-extrabold tracking-[0.12em] uppercase text-[#0a0a0f] flex items-center justify-center gap-2 disabled:opacity-60"
                style={{
                  background: `linear-gradient(180deg, hsl(${slot.hue} 85% 68%), hsl(${slot.hue} 75% 55%))`,
                  boxShadow: `0 20px 44px -10px hsl(${slot.hue} 85% 55% / 0.7), inset 0 1px 0 0 rgba(255,255,255,0.55)`,
                }}
              >
                <Zap size={16} strokeWidth={2.4} />
                {purchasing ? t('slotDeposit.processing') : t('slotDeposit.launchBtn', { amount: formatUSDT(slot.amount, locale) })}
              </button>
              {error && (
                <p className="text-[12px] text-[var(--status-negative)] text-center mt-2">{error}</p>
              )}
              <p className="text-[10px] text-[var(--text-muted)] text-center mt-3 leading-[1.6]">
                <Trans
                  i18nKey="slotDeposit.confirmHintHTML"
                  components={{ br: <br /> }}
                />
              </p>
            </div>

            <div className="px-6 py-3 border-t border-[var(--glass-stroke)] text-[9px] uppercase tracking-[0.18em] text-[var(--text-disabled)] text-center font-mono">
              нажмите вне окна — закрыть · ESC
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function StatRow({
  label,
  value,
  hue,
}: {
  label: string;
  value: string;
  hue: number;
}) {
  return (
    <div
      className="rounded-2xl px-2.5 py-2 flex flex-col gap-0.5"
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid var(--glass-stroke)',
      }}
    >
      <span className="text-[8.5px] uppercase tracking-[0.14em] text-[var(--text-muted)] font-mono font-bold">
        {label}
      </span>
      <span
        className="font-mono text-[12px] font-extrabold tabular-nums"
        style={{ color: `hsl(${hue} 80% 68%)` }}
      >
        {value}
      </span>
    </div>
  );
}
